// React Query hooks for the `field/forms/` API — see src/services/fieldForms.ts.
// Mirrors the existing hook pattern (src/api/cmTables.ts).

import { useMutation, useQuery, useQueryClient, UseQueryOptions, UseQueryResult } from "@tanstack/react-query";
import {
  createForm,
  deleteForm,
  getForm,
  listFormGroupVersions,
  listForms,
  listPublishedFormsForTable,
  PaginatedResponse,
  publishForm,
  setFormPrimaryImage,
  updateForm
} from "../services/fieldForms";
import { CreateFormPayload, FieldForm, ListFormsParams, UpdateFormPayload } from "../types/fieldForm";

const KEYS = {
  all: ["field-forms"] as const,
  list: (params?: ListFormsParams & { form_group_id?: string }) => ["field-forms", "list", params ?? {}] as const,
  detail: (uuid: string | null) => ["field-forms", "detail", uuid] as const,
  tableForms: (tableSlug: string | null) => ["field-forms", "table", tableSlug] as const,
  // Under `listRoot`, so creating / editing / publishing any form refreshes it.
  group: (formGroupId: string | null) => ["field-forms", "list", "group", formGroupId] as const,
  /** Prefix keys — invalidate ALL list / table-scoped queries without touching form details. */
  listRoot: ["field-forms", "list"] as const,
  tableRoot: ["field-forms", "table"] as const
};

/** List forms (optionally filtered by table / status / form_group_id). */
export const useListForms = (
  params?: ListFormsParams & { form_group_id?: string },
  options?: Omit<UseQueryOptions<PaginatedResponse<FieldForm>, Error>, "queryKey" | "queryFn">
): UseQueryResult<PaginatedResponse<FieldForm>, Error> => {
  return useQuery<PaginatedResponse<FieldForm>, Error>({
    queryKey: KEYS.list(params),
    queryFn: async () => (await listForms(params)).data,
    ...options
  });
};

/** Every version of one logical form (any status, newest first). */
export const useFormGroupVersions = (
  formGroupId: string | null,
  options?: Omit<UseQueryOptions<FieldForm[], Error>, "queryKey" | "queryFn">
): UseQueryResult<FieldForm[], Error> => {
  return useQuery<FieldForm[], Error>({
    queryKey: KEYS.group(formGroupId),
    enabled: formGroupId !== null,
    queryFn: async () => {
      if (!formGroupId) throw new Error("No form group id");
      return (await listFormGroupVersions(formGroupId)).data;
    },
    ...options
  });
};

/** Retrieve one form version. */
export const useForm = (
  uuid: string | null,
  options?: Omit<UseQueryOptions<FieldForm, Error>, "queryKey" | "queryFn">
): UseQueryResult<FieldForm, Error> => {
  return useQuery<FieldForm, Error>({
    queryKey: KEYS.detail(uuid),
    enabled: uuid !== null,
    queryFn: async () => {
      if (!uuid) throw new Error("No form uuid");
      return (await getForm(uuid)).data;
    },
    ...options
  });
};

/** Published forms bound to a table (for `mode:"new"` relation pickers). */
export const usePublishedFormsForTable = (
  tableSlug: string | null,
  options?: Omit<UseQueryOptions<PaginatedResponse<FieldForm>, Error>, "queryKey" | "queryFn">
): UseQueryResult<PaginatedResponse<FieldForm>, Error> => {
  return useQuery<PaginatedResponse<FieldForm>, Error>({
    queryKey: KEYS.tableForms(tableSlug),
    enabled: tableSlug !== null,
    queryFn: async () => {
      if (!tableSlug) throw new Error("No tableSlug");
      return (await listPublishedFormsForTable(tableSlug)).data;
    },
    ...options
  });
};

/** Create a draft (or a new version of an existing form_group_id). */
export const useCreateForm = () => {
  const queryClient = useQueryClient();
  return useMutation<FieldForm, Error, CreateFormPayload>({
    mutationFn: async (payload) => (await createForm(payload)).data,
    // A new form/version only affects list + relation-picker (table) queries.
    // Do NOT invalidate KEYS.all — that refetches every open form detail
    // (each linked-child editor), clobbering nothing but hammering the API.
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.listRoot });
      queryClient.invalidateQueries({ queryKey: KEYS.tableRoot });
    }
  });
};

/** Edit a draft (schema is a full replacement). */
export const useUpdateForm = () => {
  const queryClient = useQueryClient();
  return useMutation<FieldForm, Error, { uuid: string; payload: UpdateFormPayload }>({
    mutationFn: async ({ uuid, payload }) => (await updateForm(uuid, payload)).data,
    onSuccess: (form) => {
      queryClient.invalidateQueries({ queryKey: KEYS.detail(form.uuid) });
      queryClient.invalidateQueries({ queryKey: KEYS.listRoot });
    }
  });
};

/** Publish a draft (validate + freeze). */
export const usePublishForm = () => {
  const queryClient = useQueryClient();
  return useMutation<FieldForm, Error, string>({
    mutationFn: async (uuid) => (await publishForm(uuid)).data,
    onSuccess: (form) => {
      queryClient.invalidateQueries({ queryKey: KEYS.detail(form.uuid) });
      queryClient.invalidateQueries({ queryKey: KEYS.listRoot });
      queryClient.invalidateQueries({ queryKey: KEYS.tableRoot });
    }
  });
};

/** Archive / delete a form. */
export const useDeleteForm = () => {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (uuid) => {
      await deleteForm(uuid);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.listRoot });
      queryClient.invalidateQueries({ queryKey: KEYS.tableRoot });
    }
  });
};

/** Attach an uploaded media item as the form's thumbnail (no version bump). */
export const useSetFormPrimaryImage = () => {
  const queryClient = useQueryClient();
  return useMutation<void, Error, { mediaId: number; formUuid: string }>({
    mutationFn: async ({ mediaId, formUuid }) => {
      await setFormPrimaryImage(mediaId, formUuid);
    },
    onSuccess: (_data, { formUuid }) => {
      queryClient.invalidateQueries({ queryKey: KEYS.detail(formUuid) });
      queryClient.invalidateQueries({ queryKey: KEYS.listRoot });
    }
  });
};
